import store from '@polleverywhere/redux'
import { connect } from 'pwa-helpers/connect-mixin'
import { LitElement, html, property, internalProperty } from 'lit-element'
import { ifDefined } from 'lit-html/directives/if-defined'
import { register } from '@polleverywhere/hmr-custom-element'
import { bemBlock } from '@polleverywhere/bem-helpers'
import { responseDetail } from './responseDetail'
import { proxiedImageUrl } from '@polleverywhere/core/lib/util'
import { responseToListItem, humanWhen } from './util'
import { slice, LoadingState, ExportStatus } from '@polleverywhere/redux/slices/participant_response_history'

import './response-history.scss'

import '@polleverywhere/list-paginator'
import '@polleverywhere/item-list'
import '@polleverywhere/pe-breadcrumb'
import '@polleverywhere/pe-button'

store.addReducers({ [slice.name]: slice.reducer })

/**
 * Response history for a participant
 *
 * `<pec-participant-response-history />`
 *
 * The `participantResponseHistory` slice will be loaded into the main
 * `@polleverywhere/redux` store, and the caller should dispatch `fetchResponses`
 * to ensure the latest data loads
 *
 * @element pec-participant-response-history
 *
 * @attr {Boolean} [skipHeader] - Turn off the header elements (breadcrumbs, item count)
 *
 * @example <caption>Basic usage</caption>
 *   store.dispatch(slice.fetchResponses())
 *   render(html`<pec-participant-response-history></pec-participant-response-history>`, myElement)
 */
export class ResponseHistory extends connect(store)(LitElement) {
  static tagName = 'pec-participant-response-history'

  @property({ type: Boolean, attribute: 'skip-header' }) skipHeader = false

  @internalProperty({ type: String }) loading
  @internalProperty({ type: String }) exportStatus
  @internalProperty({ type: Array }) responses
  @internalProperty({ type: Object }) focusItem
  @internalProperty({ type: Object }) meta
  @internalProperty({ type: String }) presenter

  stateChanged (rootState) {
    const state = rootState[slice.name]
    this.loading = state.loading
    this.exportStatus = state.exportStatus
    this.responses = state.responses
    this.focusItem = state.focus
    this.meta = state.meta
    this.presenter = state.presenter
    this.error = state.error
  }

  createRenderRoot () { return this }

  render () {
    if (this.loading === LoadingState.pending) {
      return html`
        ${this.renderBreadcrumbs()}
        ${this.renderSkeleton()}
      `
    } else if (this.loading === LoadingState.error) {
      return html`
        ${this.renderBreadcrumbs()}
        ${this.error || 'Error loading, please retry'}
      `
    } else if (this.focusItem) {
      return html`
        ${this.renderBreadcrumbs()}
        ${this.renderDetail()}
      `
    } else {
      return html`
        ${this.renderBreadcrumbs()}
        ${this.renderList()}
      `
    }
  }

  renderBreadcrumbs () {
    return html`
      <pe-breadcrumb ?hidden=${this.skipHeader}>
        <pe-breadcrumb-item @action=${() => store.dispatch(slice.focusCleared())}>Response History</pe-breadcrumb-item>
        ${this.focusItem ? html`<pe-breadcrumb-item>Response details</pe-breadcrumb-item>` : ''}
      </pe-breadcrumb>
    `
  }

  renderSkeleton () {
    const skeletons = Array(3).fill(0).map((_, i) => {
      return { id: -1 * i, skeleton: true, subtitles: [] }
    })

    return html`
      <div ?hidden=${this.skipHeader}>
        <div class=element-skeleton-progress style="width:15em;">&nbsp;</div>
        <br />
      </div>
      <item-list readonly .collection=${skeletons}></item-list>
    `
  }

  renderList () {
    return html`
      ${this.renderListHeader()}
      ${this.renderFilterHeader()}
      <item-list
        @click:item=${(e) => store.dispatch(slice.focusResponse(e.detail.id))}
        .collection="${this.responses.map(responseToListItem)}"
      ></item-list>
      <list-paginator
        total=${this.meta.total_pages}
        current=${this.meta.current_page}
        @change:page=${(e) => store.dispatch(slice.fetchResponses(e.detail.page))}
      ></list-paginator>
      <div class=${bem('export')} ?hidden=${!this.responses.length}>
        <pe-button
          ?disabled=${this.exportStatus !== ExportStatus.initial}
          @click=${() => store.dispatch(slice.exportResponses())}
          label=${ifDefined(this.exportLabel())}></pe-button>
      </div>
    `
  }

  renderListHeader () {
    return html`
      <div class=${bem('state')} ?hidden=${this.skipHeader}>
        <div class=${bem('caption')}>
          ${this.meta.total_count} responses
        </div>
        <pe-button type=link class=${bem('refresh')} @click=${() => store.dispatch(slice.fetchResponses())} description=Refresh icon=restart></pe-button>
      </div>
    `
  }

  renderFilterHeader () {
    if (this.presenter) {
      return html`
        <div class=${bem('filter')}>
          <div class=${bem('filter-by')}>Showing responses to ${this.presenter.toUpperCase()}</div>
          <pe-button type=link class=${bem('filter-clear')} @click=${() => store.dispatch(slice.filterPresenter())} label=Clear></pe-button>
        </div>
      `
    }
    return ''
  }

  renderDetail () {
    const response = this.focusItem
    const instructionsImageUrl = response.poll.instruction_image_url ? proxiedImageUrl(response.poll.instruction_image_url, { w: 800, h: 600, fit: 'max' }) : undefined
    return html`
      <h1>Response to ${response.poll.owner_username}'s activity</h1>
      <h2 aria-describedby="aria-response-detail-question">${response.poll.title}</h2>
      <div class=${bem('detail__instructions')} ?hidden=${!instructionsImageUrl}>
        <img src=${ifDefined(instructionsImageUrl)} alt=${ifDefined(response.poll.instruction_image_alt)}>
      </div>
      <div class=${bem('detail__response')} aria-describedby="aria-response-detail-response">${responseDetail(response)}</div>
      <div class=${bem('detail__anonymous')} ?hidden=${!response.poll.anonymous}>
        <icon-element type=legacy-glyph name=anonymous></icon-element>
        This response is anonymous. The presenter cannot link it to you.
      </div>
      <div class=${bem('detail__footer')}>
        Responded
        <span class=${bem('detail__as')} ?hidden=${!response.screen_name}>as ${response.screen_name}</span>
        <span class=${bem('detail__when')}><time>on ${humanWhen(response.created_at)}</time></span>
      </div>
      <pe-button type=link class=${bem('detail__filter')} @click=${(this.handleFilterClick)} label="View all your responses to ${response.poll.owner_username}"></pe-button>

      <div id="aria-response-detail-question" hidden>Question title</div>
      <div id="aria-response-detail-response" hidden>Your response, which may include available options you didn't select</div>
    `
  }

  exportLabel () {
    switch (this.exportStatus) {
      case ExportStatus.initial:
        return 'Export responses to email'
      case ExportStatus.pending:
        return 'Exporting…'
      case ExportStatus.done:
        return 'Export complete'
      case ExportStatus.error:
        return 'Error exporting'
    }
  }

  handleFilterClick () {
    if (this.focusItem?.poll.owner_username) {
      store.dispatch(slice.filterPresenter(this.focusItem?.poll.owner_username))
    }
  }
}

const bem = bemBlock(ResponseHistory.tagName)
register(module, ResponseHistory.tagName, ResponseHistory)
